import React, { ReactNode, createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Keycloak from 'keycloak-js';

interface KeycloakContextValue {
  keycloak: Keycloak;
  initialized: boolean;
}

const KeycloakContext = createContext<KeycloakContextValue | null>(null);

export const useKeycloak = () => {
  const context = useContext(KeycloakContext);
  if (!context) {
    throw new Error('useKeycloak must be used within a KeycloakProvider');
  }
  return context;
};

interface KeycloakProviderProps {
  keycloak: Keycloak;
  initOptions?: Keycloak.KeycloakInitOptions;
  children: ReactNode;
}

/**
 * Custom Keycloak provider that works with React 18 StrictMode.
 * Prevents double initialization that would occur with @react-keycloak/web.
 */
export const KeycloakProvider: React.FC<KeycloakProviderProps> = ({
  keycloak,
  initOptions,
  children,
}) => {
  const [initialized, setInitialized] = useState(false);
  const initStartedRef = useRef(false);

  useEffect(() => {
    // Prevent double initialization in StrictMode
    if (initStartedRef.current) {
      return;
    }
    initStartedRef.current = true;

    const initKeycloak = async () => {
      try {
        await keycloak.init(initOptions || {});
        setInitialized(true);
      } catch (error) {
        console.error('Failed to initialize Keycloak:', error);
        // Still set initialized to true so app can show error state
        setInitialized(true);
      }
    };

    initKeycloak();
  }, [keycloak, initOptions]);

  const value = useMemo(
    () => ({
      keycloak,
      initialized,
    }),
    [keycloak, initialized]
  );

  return (
    <KeycloakContext.Provider value={value}>
      {children}
    </KeycloakContext.Provider>
  );
};
